import { observer } from "mobx-react-lite";
import React from "react";
import { Container, Grid, Header, Table } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { useStore } from "../../../app/stores/store";
import {
  convertTicketTypeToString,
  formatCurrency,
} from "../../../app/common/util/functions";
import PageHeader from "../../../app/layout/PageHeader";
import {
  InventoryTransactionType,
  TicketType,
} from "../../../app/models/enums";

interface Props {
  startDate: Date;
  endDate: Date;
}

export default observer(function InventoryTransactionPrint({
  startDate,
  endDate,
}: Props) {
  const { t } = useTranslation(["common", "reports"]);

  const { inventoryTransactionsReportStore } = useStore();
  const {
    groupedInventoryTransactionsReport,
    calculateTotalCosts,
    getInventoryTransactionsReport,
    calculateTotalQuantity,
  } = inventoryTransactionsReportStore;

  return (
    <>
      <Container className="page">
        <Grid stackable columns={2} className="headerPrint">
          <Grid.Row>
            <Grid.Column>
              <PageHeader
                header={`${t("report", { ns: "reports" })}: ${t(
                  "inv_transactions",
                  {
                    ns: "reports",
                  }
                )}`}
                type="h2"
                divider={false}
                getAlerts={true}
                addTitle={true}
              />
            </Grid.Column>
            <Grid.Column>
              <Header
                content={`${format(startDate, "PP")} - ${format(
                  endDate,
                  "PP"
                )}`}
                as="h2"
                textAlign="right"
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
        {groupedInventoryTransactionsReport &&
          groupedInventoryTransactionsReport.length > 0 && (
            <Table striped unstackable className="dnxTable">
              {groupedInventoryTransactionsReport.map(
                ([group, items], groupIndex) => (
                  <React.Fragment key={groupIndex}>
                    <>
                      <Table.Body>
                        <Table.Row>
                          <Table.Cell
                            colSpan={12}
                            textAlign="left"
                            className="terms_bold"
                          >
                            {group}
                          </Table.Cell>
                        </Table.Row>
                      </Table.Body>
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell>
                            {t("ticketnum", { ns: "common" })}
                          </Table.HeaderCell>
                          <Table.HeaderCell width={2}>
                            {t("location", { ns: "common" })}
                          </Table.HeaderCell>
                          <Table.HeaderCell width={2}>
                            {t("part_number", { ns: "reports" })}
                          </Table.HeaderCell>
                          <Table.HeaderCell width={6}>
                            {t("description", { ns: "common" })}
                          </Table.HeaderCell>
                          <Table.HeaderCell textAlign="center" width={2}>
                            {t("vendor.header", { ns: "reports" })}
                          </Table.HeaderCell>
                          <Table.HeaderCell textAlign="right">
                            {t("pops.qty", { ns: "tickets" })}
                          </Table.HeaderCell>
                          <Table.HeaderCell textAlign="right" width={2}>
                            {t("total_cost", { ns: "reports" })}
                          </Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                      {items.map((item) => (
                        <Table.Row key={item.id}>
                          <Table.Cell
                            className={
                              item.ticketNumber &&
                              // eslint-disable-next-line eqeqeq
                              item.transactionType ==
                                InventoryTransactionType.Sale
                                ? "hoveredLink"
                                : ""
                            }
                            onClick={() =>
                              item.ticketNumber &&
                              // eslint-disable-next-line eqeqeq
                              item.transactionType ==
                                InventoryTransactionType.Sale
                                ? window.open(
                                    `/ticket/view/${item.ticketId}`,
                                    `_`
                                  )
                                : console.log("")
                            }
                            data-label={`${t("invnum", { ns: "common" })}:`}
                          >
                            {item.ticketNumber &&
                            // eslint-disable-next-line eqeqeq
                            item.transactionType ==
                              InventoryTransactionType.Sale
                              ? `${convertTicketTypeToString(
                                  item.ticketType ?? TicketType.Invoice
                                )} #${item.ticketNumber}`
                              : ``}
                          </Table.Cell>
                          <Table.Cell
                            data-label={`${t("location", {
                              ns: "reports",
                            })}:`}
                            width={2}
                          >
                            {item.location}
                          </Table.Cell>
                          <Table.Cell
                            className={"hoveredLink"}
                            onClick={() =>
                              window.open(
                                `/inventoryIndex/partDetails/${item.partId}`,
                                `_`
                              )
                            }
                            data-label={`${t("part_number", {
                              ns: "reports",
                            })}:`}
                            width={2}
                          >
                            {item.partNumber}
                          </Table.Cell>
                          <Table.Cell
                            width={6}
                            data-label={`${t("description", {
                              ns: "common",
                            })}:`}
                          >
                            {item.partDescription}
                          </Table.Cell>
                          <Table.Cell
                            data-label={`${t("vendor.header", {
                              ns: "reports",
                            })}:`}
                            textAlign="center"
                            width={2}
                          >
                            {item.vendorName}
                          </Table.Cell>
                          <Table.Cell
                            textAlign="right"
                            data-label={`${t("pops.qty", { ns: "tickets" })}:`}
                          >
                            {item.quantity}
                          </Table.Cell>
                          <Table.Cell
                            data-label={`${t("total_cost", {
                              ns: "reports",
                            })}:`}
                            textAlign="right"
                            width={2}
                          >
                            {formatCurrency(item.transactionAmount)}
                          </Table.Cell>
                        </Table.Row>
                      ))}
                      <Table.Row className="terms_bold">
                        <Table.Cell>{t("total", { ns: "common" })}</Table.Cell>
                        <Table.Cell textAlign="right"></Table.Cell>
                        <Table.Cell textAlign="right"></Table.Cell>
                        <Table.Cell textAlign="right"></Table.Cell>
                        <Table.Cell textAlign="right"></Table.Cell>
                        <Table.Cell textAlign="right"></Table.Cell>
                        <Table.Cell
                          textAlign="right"
                          data-label={`${t("cost", { ns: "reports" })}:`}
                        >
                          {formatCurrency(calculateTotalCosts(items))}
                        </Table.Cell>
                      </Table.Row>
                      <Table.Row className="noBackground">
                        <Table.Cell colSpan={11}></Table.Cell>
                      </Table.Row>
                      </Table.Body>
                    </>
                  </React.Fragment>
                )
              )}
              <Table.Row className="noBackground">
                <Table.Cell colSpan={11}></Table.Cell>
              </Table.Row>
              <Table.Row className="terms_bold greenRow">
                <Table.Cell>{t("grandTotals", { ns: "reports" })}</Table.Cell>
                <Table.Cell textAlign="right"></Table.Cell>
                <Table.Cell textAlign="right"></Table.Cell>
                <Table.Cell textAlign="right"></Table.Cell>
                <Table.Cell textAlign="right"></Table.Cell>
                <Table.Cell
                  textAlign="right"
                  data-label={`${t("pops.qty", { ns: "tickets" })}:`}
                >
                  {calculateTotalQuantity(getInventoryTransactionsReport)}
                </Table.Cell>
                <Table.Cell
                  textAlign="right"
                  data-label={`${t("cost", { ns: "reports" })}:`}
                >
                  {formatCurrency(
                    calculateTotalCosts(getInventoryTransactionsReport)
                  )}
                </Table.Cell>
              </Table.Row>
            </Table>
          )}
      </Container>
    </>
  );
});
